import { Injectable } from '@angular/core';
import { shareReplayCold } from '@principle-theorem/shared';
import { BehaviorSubject, merge, type Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { BreakpointService } from '../breakpoint.service';
import {
  DesktopExpandedState,
  DesktopMinimisedState,
  type ISidebarState,
  MobileClosedState,
  MobileOpenState,
  type SideBarMode,
} from './sidebar-states';

@Injectable({
  providedIn: 'root',
})
export class SidebarManagerService {
  private readonly _animationDelayMs: number = 400;
  mobileState$ = new BehaviorSubject<ISidebarState>(new MobileClosedState());
  desktopState$ = new BehaviorSubject<ISidebarState>(
    new DesktopExpandedState()
  );
  state$: Observable<ISidebarState>;
  animations$: Observable<Promise<unknown>>;
  isMobile$: Observable<boolean>;
  isTablet$: Observable<boolean>;
  isDesktop$: Observable<boolean>;
  mode$: Observable<SideBarMode>;
  expanded$: Observable<boolean>;
  opened$: Observable<boolean>;

  constructor(public breakpoint: BreakpointService) {
    this.isDesktop$ = this.breakpoint.isDesktop$.pipe(shareReplayCold());
    this.isMobile$ = this.isDesktop$.pipe(
      map((isDesktop) => !isDesktop),
      shareReplayCold()
    );

    this.state$ = merge(
      this.isMobile$.pipe(
        filter((isMobile) => isMobile),
        switchMap(() => this.mobileState$)
      ),
      this.isDesktop$.pipe(
        filter((isDesktop) => isDesktop),
        switchMap(() => this.desktopState$)
      )
    ).pipe(shareReplayCold());

    this.animations$ = merge(this.state$).pipe(
      map(() => this._animationComplete())
    );

    this.mode$ = this.state$.pipe(
      map((state) => state.mode),
      shareReplayCold()
    );
    this.expanded$ = this.state$.pipe(
      map((state) => state.expanded),
      shareReplayCold()
    );
    this.opened$ = this.state$.pipe(
      map((state) => state.opened),
      shareReplayCold()
    );
  }

  open(): void {
    this.mobileState$.next(new MobileOpenState());
  }

  close(): void {
    this.mobileState$.next(new MobileClosedState());
  }

  expand(): void {
    this.desktopState$.next(new DesktopExpandedState());
  }

  minimise(): void {
    this.desktopState$.next(new DesktopMinimisedState());
  }

  toggleExpanded(): void {
    if (this.desktopState$.value.expanded) {
      return this.minimise();
    }
    this.expand();
  }

  toggleOpen(): void {
    if (this.mobileState$.value.opened) {
      return this.close();
    }
    this.open();
  }

  private _animationComplete(): Promise<void> {
    return new Promise((resolve: () => void) => {
      setTimeout(resolve, this._animationDelayMs);
    });
  }
}
